import { type ReactNode } from "react";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { useExternalContent } from "../hooks/useExternalContent";
import {
  CURRENT_LANGUAGE,
  ENGLISH_EXTERNAL_CONTENT_SUFFIX,
  GERMAN_EXTERNAL_CONTENT_SUFFIX,
  GERMAN_LANGUAGE,
} from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import {
  useForcedConfiguratorTranslations,
  useXmlConfiguratorTranslations,
} from "~/Configurators/common/useXmlConfiguratorTranslations";
import classNames from "classnames";
import { removePTags } from "~/utils/domUtils";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  content: string | undefined | null;
  error?: ReactNode; // What to show in case of error - set to null to not show anything
  loading?: ReactNode; // What to show when loading - set to null to not show anything
  loadedClassname?: string; // Classname applied to the loaded element
  as?: keyof JSX.IntrinsicElements;
  removePtags?: boolean;
  forceXmlConfiguratorCode?: string;
}

const TranslatedContent = ({
  content,
  loadedClassname,
  as = "div",
  removePtags,
  forceXmlConfiguratorCode,
  ...rest
}: Props) => {
  const { t } = forceXmlConfiguratorCode
    ? useForcedConfiguratorTranslations(forceXmlConfiguratorCode)
    : useXmlConfiguratorTranslations();
  if (t) {
    content = content ? t(content) : content;
    const Tag = as;
    return (
      <Tag
        className={classNames(loadedClassname, "scha__lc__external-content--shaft")}
        dangerouslySetInnerHTML={{ __html: removePtags ? removePTags(content!)! : content! }}
      />
    );
  }
  return <LanguageAwareExternalContent content={content} loadedClassname={loadedClassname} {...rest} />;
};

const LanguageAwareExternalContent = ({
  error = <DefaultErrorMsg />,
  loading = <CircleSpinner />,
  content,
  loadedClassname,
  as = "div",
  removePtags,
}: Props) => {
  content = resolveUrlLanguage(content);
  const { state, content: externalContent } = useExternalContent(content);

  const Tag = as;

  return (
    <>
      {state === "loading" && loading}
      {state === "error" && error}
      {state === "loaded" && (
        <Tag
          className={loadedClassname}
          dangerouslySetInnerHTML={{ __html: removePtags ? removePTags(externalContent!)! : externalContent! }}
        />
      )}
    </>
  );
};
export default TranslatedContent;

const DefaultErrorMsg = () => {
  const { t } = useTranslation();
  return <Alert severity={"error"}>{t("externalContent.error")}</Alert>;
};

export const resolveUrlLanguage = (url: string | undefined | null, targetLanguage = CURRENT_LANGUAGE) => {
  // We need to enforce the language change here. There is a complex bug in the BE
  // because of which we are not able to switch the language for some of the characteristics.
  // This is just a temporary fix and this component should be removed when that bug is fixed.
  if (url) {
    if (targetLanguage == GERMAN_LANGUAGE) {
      return url.replace(ENGLISH_EXTERNAL_CONTENT_SUFFIX, GERMAN_EXTERNAL_CONTENT_SUFFIX);
    } else {
      return url.replace(GERMAN_EXTERNAL_CONTENT_SUFFIX, ENGLISH_EXTERNAL_CONTENT_SUFFIX);
    }
  }
  return url;
};
